import React, {
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import ImageGallery from 'react-image-gallery';

import {
  FaExpand,
  FaPlayCircle,
  FaChevronCircleLeft,
  FaChevronCircleRight,
} from 'react-icons/fa';
import { useUiComponentsInternationalContext } from '@rsc/static-translations';

import breakpoints from '../breakpoints';
import Video from './Video';

const Wrapper = styled.div`
  ${({ numberOfImages }) =>
    numberOfImages <= 1 &&
    css`
      .image-gallery-thumbnails {
        display: none;
      }
    `}

  .image-gallery-svg {
    width: 30px;
  }

  .image-gallery-content:not(.fullscreen) {
    .image-gallery-swipe {
      aspect-ratio: 1/1;
      display: flex;
      align-items: center;
      background-color: #000;
    }

    .image-gallery-slides {
      width: 100%;
      touch-action: auto;
    }
  }

  .image-gallery-thumbnail {
    cursor: pointer;
    height: 100%;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }

    &.active,
    &:hover,
    &:focus,
    &:active {
      border-color: ${({ theme }) => theme.colors.brandPrimary};
      size: 50%;
    }
  }

  .image-gallery-icon {
    z-index: 1;
    filter: unset;
    padding: 0;
    color: #fff;

    svg {
      fill: #fff;
      stroke: #000;
      stroke-width: 3px;
    }

    &:hover {
      color: ${({ theme }) => theme.colors.brandPrimary};

      svg {
        fill: ${({ theme }) => theme.colors.brandPrimary};
      }
    }
  }

  .image-gallery-left-nav,
  .image-gallery-right-nav {
    position: absolute;
    padding: 0;
    top: 1.5rem;

    @media (min-width: ${breakpoints.md}) {
      top: 2.5rem;
    }

    @media (min-width: ${breakpoints.lg}) {
      top: 2.65rem;
    }
  }

  .image-gallery-right-nav {
    left: 0.5rem;
    right: unset;
    margin-left: 2rem;
  }

  .image-gallery-left-nav {
    left: 0.5rem;
  }

  .fullscreen-modal {
    z-index: 9995;
  }

  .fullscreen {
    .image-gallery-left-nav,
    .image-gallery-right-nav {
      top: 1.5rem;
    }
    .image-gallery-slide-wrapper {
      padding: 0;
    }
    .image-gallery-thumbnail {
      height: 72px;

      img {
        height: 63px;
      }
    }

    .react-player__preview {
      background-size: contain !important;
      background-repeat: no-repeat;
    }

    .react-player-wrapper {
      padding-top: 0;
      height: calc(100vh - 90px);

      .react-player {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
      }
    }
  }

  .image-gallery-fullscreen-button {
    position: absolute;
    right: 0.5rem;
    z-index: 1;
    bottom: ${({ showBullets }) => (showBullets ? `2.25rem` : '0.5rem')};
  }

  .image-gallery-bullets {
    position: static;
    margin: 0.5rem auto;

    .image-gallery-bullet {
      box-shadow: none;
      background: ${({ theme }) => theme.colors.carouselBullet};
      border: 1px solid ${({ theme }) => theme.colors.carouselBullet};
      border-radius: 50%;
      padding: 0.25rem;

      &.active,
      &:hover,
      &:focus,
      &.active:hover {
        transform: scale(1.2);
        border: 1px solid ${({ theme }) => theme.colors.carouselBulletHover};
        background: ${({ theme }) => theme.colors.carouselBulletHover};
      }
    }
  }
`;

const VideoThumbContainer = styled.div`
  position: relative;
  display: inline-block;

  .play-icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: rgba(255, 255, 255, 0.8);
    width: 25px;
    height: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const renderVideoThumb = item => (
  <VideoThumbContainer>
    <img
      className="image-gallery-thumbnail-image"
      src={item?.thumbnail}
      height="92"
      width="92"
      alt={item?.originalAlt}
    />
    <FaPlayCircle className="play-icon" />
  </VideoThumbContainer>
);

function ProductMediaCarousel({
  media,
  showNav,
  showFullscreenButton,
  showBullets,
  showThumbnails,
  selectedSlide,
  thumbnailPosition,
}) {
  const imageGallery = useRef();
  const videoRef = useRef();

  const { tt } = useUiComponentsInternationalContext();

  const renderLeftNav = useCallback(
    (onClick, disabled) => (
      <button
        type="button"
        className="image-gallery-icon image-gallery-left-nav"
        aria-label={tt(s => s.productCommon.previousSlideAriaLabel)}
        onClick={onClick}
      >
        <FaChevronCircleLeft disabled={disabled} size="25" />
      </button>
    ),
    [tt]
  );

  const renderRightNav = useCallback(
    (onClick, disabled) => (
      <button
        type="button"
        className="image-gallery-icon image-gallery-right-nav"
        aria-label={tt(s => s.productCommon.nextSlideAriaLabel)}
        onClick={onClick}
      >
        <FaChevronCircleRight disabled={disabled} size="25" />
      </button>
    ),
    [tt]
  );

  const [currentSlide, setCurrentSlide] = useState(0);

  const renderFullscreen = (onClick, isFullscreen) => (
    <button
      type="button"
      className="image-gallery-icon image-gallery-fullscreen-button"
      aria-label={tt(s => s.productCommon.expandToFullscreenAriaLabel)}
      onClick={onClick}
    >
      <FaExpand onClick={onClick} isFullscreen={isFullscreen} size="25" />
    </button>
  );

  const handleOnSlide = currentIndex => {
    setCurrentSlide(currentIndex);
  };

  const renderVideo = useCallback(item => {
    if (item?.type === 'ShopifyVideo') {
      return (
        item?.sources?.length > 0 && (
          <Video
            url={item.sources.map(source => source?.url || '')}
            index={item.index}
            poster={item.poster}
          />
        )
      );
    }
    return (
      item?.embeddedUrl && (
        <Video url={item.embeddedUrl} index={item.index} poster={item.poster} />
      )
    );
  }, []);

  const productMedia = useMemo(
    () =>
      media?.map((mediaAsset, index) => {
        if (mediaAsset.__typename === 'ShopifyVideo') {
          return {
            type: mediaAsset?.__typename,
            index,
            ref: videoRef,
            sources: mediaAsset?.sources,
            thumbnail: `${mediaAsset?.preview?.image?.src}&width=92`,
            original: `${mediaAsset?.preview?.image?.src}&width=624`,
            originalHeight: '624',
            originalWidth: '624',
            thumbnailHeight: '92',
            thumbnailWidth: '92',
            originalAlt: mediaAsset?.preview?.image?.altText || '',
            renderItem: renderVideo,
            renderThumbInner: renderVideoThumb,
            poster: mediaAsset?.preview?.image?.src,
          };
        }

        if (mediaAsset.__typename === 'ShopifyExternalVideo') {
          return {
            type: mediaAsset?.__typename,
            index,
            ref: videoRef,
            embeddedUrl: mediaAsset?.embeddedUrl,
            thumbnail: `${mediaAsset?.preview?.image?.src}&width=92`,
            original: `${mediaAsset?.preview?.image?.src}&width=624`,
            originalHeight: '624',
            originalWidth: '624',
            thumbnailHeight: '92',
            thumbnailWidth: '92',
            originalAlt: mediaAsset?.preview?.image?.altText || '',
            renderItem: renderVideo,
            renderThumbInner: renderVideoThumb,
            poster: mediaAsset?.preview?.image?.src,
          };
        }

        return {
          type: mediaAsset?.__typename,
          index,
          thumbnail: `${mediaAsset?.image?.src}&width=92`,
          original: `${mediaAsset?.image?.src}&width=624`,
          sizes: '100vw',
          originalHeight: '624',
          originalWidth: '624',
          thumbnailHeight: '92',
          thumbnailWidth: '92',
          originalAlt: mediaAsset?.image?.altText || '',
        };
      }),
    [media, renderVideo, videoRef, renderVideoThumb]
  );

  useEffect(() => {
    // Disable tabIndex for controls on hidden slides
    const slides = document.querySelectorAll('.image-gallery-slide');
    slides.forEach(slide => {
      const inputsAndButtons = slide.querySelectorAll(
        'input, button, .react-player__preview'
      );

      if (!slide.classList.contains('image-gallery-center')) {
        inputsAndButtons.forEach(element => {
          element.setAttribute('tabindex', '-1');
        });
      } else {
        inputsAndButtons.forEach(element => {
          element.setAttribute('tabindex', '0');
        });
      }
    });
  }, [currentSlide]);

  return (
    <Wrapper numberOfImages={productMedia.length} showBullets={showBullets}>
      <ImageGallery
        items={productMedia}
        lazyLoad
        showNav={showNav}
        showBullets={showBullets}
        showFullscreenButton={showFullscreenButton}
        showPlayButton={false}
        ref={imageGallery}
        selectedSlide={selectedSlide}
        showThumbnails={showThumbnails}
        thumbnailPosition={thumbnailPosition}
        renderLeftNav={renderLeftNav}
        renderRightNav={renderRightNav}
        onSlide={handleOnSlide}
        startIndex={currentSlide}
        renderFullscreenButton={renderFullscreen}
      />
    </Wrapper>
  );
}

ProductMediaCarousel.propTypes = {
  media: PropTypes.arrayOf(
    PropTypes.shape({
      src: PropTypes.string,
      __typename: PropTypes.string,
    })
  ),
  showNav: PropTypes.bool,
  showThumbnails: PropTypes.bool,
  showBullets: PropTypes.bool,
  showFullscreenButton: PropTypes.bool,
  selectedSlide: PropTypes.number,
  thumbnailPosition: PropTypes.string,
};

ProductMediaCarousel.defaultProps = {
  media: [],
  showNav: false,
  showBullets: false,
  showThumbnails: true,
  showFullscreenButton: true,
  selectedSlide: 0,
  thumbnailPosition: 'bottom',
};

export default ProductMediaCarousel;
